import { useState, useEffect, useCallback } from "react";
import { ShieldCheck, Check, X, ChevronDown, ChevronUp, FileText, Lock, BookOpen, Loader2 } from "lucide-react";
import { supabase } from "../supabase";
import type { Session } from "@supabase/supabase-js";

interface ConsentGateProps {
  t: (k: string) => string;
  dir: "rtl" | "ltr";
  session: Session | null;
  onAccept: () => void;
  onDecline: () => void;
}

const STORAGE_KEY = "consent_version_v1";

export interface ConsentVersion {
  document_type: string;
  version: string;
  content: string;
  updated_at: string;
}

export function getStoredConsentVersion(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}

export function setStoredConsentVersion(v: string) {
  try { localStorage.setItem(STORAGE_KEY, v); } catch {}
}

export function clearStoredConsentVersion() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

/** Returns true if the user has accepted the latest version of all documents. */
export async function checkConsentCurrent(session: Session | null): Promise<boolean> {
  try {
    const { data: versions } = await supabase
      .from("consent_versions")
      .select("document_type, version")
      .order("document_type");

    if (!versions || versions.length === 0) return true;

    const latestKey = versions.map(v => `${v.document_type}:${v.version}`).join("|");

    if (session?.user?.id) {
      const { data: consent } = await supabase
        .from("user_consents")
        .select("terms_version, privacy_version, guidelines_version")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (!consent) return false;

      const userKey = [
        `terms:${consent.terms_version}`,
        `privacy:${consent.privacy_version}`,
        `guidelines:${consent.guidelines_version}`,
      ].join("|");

      return userKey === latestKey;
    }

    return getStoredConsentVersion() === latestKey;
  } catch {
    return getStoredConsentVersion() !== null;
  }
}

export function ConsentGate({ t, dir, session, onAccept, onDecline }: ConsentGateProps) {
  const [view, setView] = useState<"consent" | "privacy" | "terms" | "guidelines">("consent");
  const [expandedPrivacy, setExpandedPrivacy] = useState(false);
  const [expandedTerms, setExpandedTerms] = useState(false);
  const [expandedGuidelines, setExpandedGuidelines] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docVersions, setDocVersions] = useState<ConsentVersion[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  const fetchDocs = useCallback(async () => {
    const { data } = await supabase
      .from("consent_versions")
      .select("*")
      .order("document_type");
    if (data) setDocVersions(data as ConsentVersion[]);
    setLoadingDocs(false);
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const termsDoc = docVersions.find(d => d.document_type === "terms");
  const privacyDoc = docVersions.find(d => d.document_type === "privacy");
  const guidelinesDoc = docVersions.find(d => d.document_type === "guidelines");

  const handleAccept = async () => {
    if (!agreed) return;
    setSaving(true);

    const termsV = termsDoc?.version || "1.0.0";
    const privacyV = privacyDoc?.version || "1.0.0";
    const guidelinesV = guidelinesDoc?.version || "1.0.0";

    const versionKey = `terms:${termsV}|privacy:${privacyV}|guidelines:${guidelinesV}`;
    setStoredConsentVersion(versionKey);

    if (session?.user?.id) {
      await supabase.from("user_consents").upsert({
        user_id: session.user.id,
        terms_version: termsV,
        privacy_version: privacyV,
        guidelines_version: guidelinesV,
        accepted_at: new Date().toISOString(),
      }, { onConflict: "user_id" });

      await supabase.from("user_profiles").update({
        consent_accepted: true,
        consent_version: versionKey,
        consent_date: new Date().toISOString(),
      }).eq("user_id", session.user.id);
    }

    setSaving(false);
    onAccept();
  };

  const buildSections = (prefix: string, count: number) => {
    const sections: { title: string; body: string }[] = [];
    for (let i = 1; i <= count; i++) {
      const title = t(`${prefix}Sec${i}Title`);
      const body = t(`${prefix}Sec${i}Body`);
      if (title && body) sections.push({ title, body });
    }
    return sections;
  };

  const renderDocument = (
    introKey: string,
    sections: { title: string; body: string }[],
    expanded: boolean,
    setExpanded: (v: boolean) => void,
  ) => {
    if (sections.length === 0) return null;
    return (
      <>
        <p className="text-sm text-stone-600 dark:text-slate-300 leading-relaxed text-center font-medium">
          {t(introKey)}
        </p>
        <div className="rounded-2xl border border-stone-200 dark:border-slate-700 p-3.5 space-y-1.5">
          <p className="text-xs font-bold text-stone-800 dark:text-slate-100">{sections[0].title}</p>
          <p className="text-xs text-stone-600 dark:text-slate-400 leading-relaxed whitespace-pre-line">{sections[0].body}</p>
        </div>
        {sections.length > 1 && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-600 dark:text-slate-300 text-xs font-semibold hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors"
            >
              <span>{t("consentViewFull")}</span>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {expanded && (
              <div className="space-y-2.5 animate-in fade-in duration-200">
                {sections.slice(1).map((s, i) => (
                  <div key={i} className="rounded-2xl border border-stone-200 dark:border-slate-700 p-3.5 space-y-1.5">
                    <p className="text-xs font-bold text-stone-800 dark:text-slate-100">{s.title}</p>
                    <p className="text-xs text-stone-600 dark:text-slate-400 leading-relaxed whitespace-pre-line">{s.body}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </>
    );
  };

  const privacySections = buildSections("privacy", 6);
  const termsSections = buildSections("terms", 5);
  const guidelinesSections = buildSections("guidelines", 5);

  return (
    <div className="fixed inset-0 z-[350] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" dir={dir}>
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0A2240] to-[#0D1B2E] px-6 py-6 flex flex-col items-center gap-3 shrink-0">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
            {view === "consent" && <ShieldCheck className="w-7 h-7 text-emerald-400" />}
            {view === "privacy" && <Lock className="w-7 h-7 text-emerald-400" />}
            {view === "terms" && <FileText className="w-7 h-7 text-emerald-400" />}
            {view === "guidelines" && <BookOpen className="w-7 h-7 text-emerald-400" />}
          </div>
          <h2 className="text-lg font-bold text-white text-center">
            {view === "consent" ? t("consentTitle") : view === "privacy" ? t("privacyPolicyTitle") : view === "terms" ? t("termsTitle") : t("guidelinesTitle")}
          </h2>
          {view !== "consent" && (
            <span className="text-[10px] text-white/50 font-mono">
              v{view === "privacy" ? privacyDoc?.version : view === "terms" ? termsDoc?.version : guidelinesDoc?.version}
            </span>
          )}
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {loadingDocs ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
            </div>
          ) : view === "consent" ? (
            <>
              <p className="text-sm text-stone-600 dark:text-slate-300 leading-relaxed text-center font-medium">
                {t("consentIntro")}
              </p>

              <div className="rounded-2xl border border-emerald-200 dark:border-emerald-700/40 bg-emerald-50 dark:bg-emerald-900/10 p-3.5 space-y-1.5">
                <p className="text-xs font-bold text-stone-800 dark:text-slate-100">{t("consentLocalTitle")}</p>
                <p className="text-xs text-stone-600 dark:text-slate-400 leading-relaxed">{t("consentLocalBody")}</p>
              </div>

              <div className="rounded-2xl border border-blue-200 dark:border-blue-700/40 bg-blue-50 dark:bg-blue-900/10 p-3.5 space-y-1.5">
                <p className="text-xs font-bold text-stone-800 dark:text-slate-100">{t("consentCloudTitle")}</p>
                <p className="text-xs text-stone-600 dark:text-slate-400 leading-relaxed">{t("consentCloudBody")}</p>
              </div>

              <div className="rounded-2xl border border-amber-200 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-900/10 p-3.5 space-y-1.5">
                <p className="text-xs font-bold text-stone-800 dark:text-slate-100">{t("consentRightsTitle")}</p>
                <p className="text-xs text-stone-600 dark:text-slate-400 leading-relaxed">{t("consentRightsBody")}</p>
              </div>

              {/* Three document buttons */}
              <div className="grid grid-cols-3 gap-2 pt-1">
                <button
                  onClick={() => setView("terms")}
                  className="flex flex-col items-center gap-1 px-2 py-3 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-700 dark:text-slate-200 text-[11px] font-semibold hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  {t("termsBtn")}
                </button>
                <button
                  onClick={() => setView("privacy")}
                  className="flex flex-col items-center gap-1 px-2 py-3 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-700 dark:text-slate-200 text-[11px] font-semibold hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors"
                >
                  <Lock className="w-4 h-4" />
                  {t("privacyPolicyBtn")}
                </button>
                <button
                  onClick={() => setView("guidelines")}
                  className="flex flex-col items-center gap-1 px-2 py-3 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-700 dark:text-slate-200 text-[11px] font-semibold hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  {t("guidelinesBtn")}
                </button>
              </div>

              <p className="text-[11px] text-stone-500 dark:text-slate-400 leading-relaxed text-center pt-1">
                {t("consentAcceptance")}
              </p>
            </>
          ) : view === "privacy" ? (
            <>
              {renderDocument("privacyIntro", privacySections, expandedPrivacy, setExpandedPrivacy)}
              <button onClick={() => setView("consent")} className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-700 dark:text-slate-200 text-xs font-semibold hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors">
                {t("consentBack")}
              </button>
            </>
          ) : view === "terms" ? (
            <>
              {renderDocument("termsIntro", termsSections, expandedTerms, setExpandedTerms)}
              <button onClick={() => setView("consent")} className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-700 dark:text-slate-200 text-xs font-semibold hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors">
                {t("consentBack")}
              </button>
            </>
          ) : (
            <>
              {renderDocument("guidelinesIntro", guidelinesSections, expandedGuidelines, setExpandedGuidelines)}
              <button onClick={() => setView("consent")} className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-700 dark:text-slate-200 text-xs font-semibold hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors">
                {t("consentBack")}
              </button>
            </>
          )}
        </div>

        {/* Footer — only on consent view */}
        {view === "consent" && (
          <div className="px-5 pb-5 pt-3 shrink-0 border-t border-stone-100 dark:border-slate-800 space-y-3">
            {/* Checkbox */}
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded-md border-2 border-stone-300 text-emerald-500 focus:ring-emerald-400 shrink-0"
              />
              <span className="text-xs text-stone-600 dark:text-slate-300 leading-relaxed">
                {t("consentCheckbox")}
              </span>
            </label>

            <button
              onClick={handleAccept}
              disabled={!agreed || saving}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:from-stone-300 disabled:to-stone-400 disabled:cursor-not-allowed text-white font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {t("consentAccept")}
            </button>
            <button
              onClick={onDecline}
              className="w-full py-2.5 rounded-xl bg-stone-100 dark:bg-slate-800 text-stone-500 dark:text-slate-400 font-semibold text-sm hover:bg-stone-200 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              {t("consentDecline")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
